import React from 'react';
import { TCN } from './tokens';

/**
 * TCN Delta — ↑/↓ + percentage with auto-color.
 * By default: positive = green, negative = red.
 * For metrics where a decrease is good (e.g. churn rate), pass `good="down"`
 * to invert the color rule.
 *
 * Props:
 *  - value: number — signed percentage delta (e.g. 12.5 for +12.5%, -3 for -3%)
 *  - good: 'up' | 'down' (default 'up')
 *  - prefix, suffix: optional adornments
 *  - showSign: include explicit '+' for positive (default true)
 *  - mono: tabular-nums (default true)
 */
const Delta = ({
  value,
  good = 'up',
  prefix,
  suffix = '%',
  showSign = true,
  mono = true,
  className = '',
  style,
}) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;

  const num = Number(value);
  const isUp = num > 0;
  const isDown = num < 0;
  const isPositive = good === 'up' ? isUp : isDown;
  const isNegative = good === 'up' ? isDown : isUp;

  const color = isPositive
    ? TCN.green
    : isNegative
    ? TCN.red
    : TCN.textDim;

  const arrow = num === 0 ? '·' : isUp ? '↑' : '↓';
  const formatted = `${showSign && num > 0 ? '+' : ''}${num}`;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        color,
        fontSize: 12,
        fontWeight: 700,
        fontFamily: mono ? TCN.fontMono : TCN.fontSans,
        fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
        ...style,
      }}
    >
      <span aria-hidden="true">{arrow}</span>
      {prefix}
      <span>{formatted}{suffix}</span>
    </span>
  );
};

export default Delta;
